import Logo from '@anm/components/Logo';
import NewStreamButton from '@anm/components/stream/NewStreamButton';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { getLinkTarget } from '@anm/helpers/upgradeLink';
import * as semrush from '@anm/helpers/semrush';
import useSwitchState from '@anm/hooks/useSwitchState';
import React, { useCallback, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTranslation from 'next-translate/useTranslation';
import config from '../../../../config';
import vtConfig from '../../../../config';
import { brandActions } from '../../../../store/modules/brands';
import { hostedVideoSelectors } from '../../../../store/modules/hostedVideo';
import { projectSelectors } from '../../../../store/modules/project';
import { userSelectors } from '../../../../store/modules/user';
import AuthUserMenuBlock from '../../../auth/AuthUserMenuBlock';
import useStorageError from '../../../my-projects/MyStreamsSection/hooks/useStorageError';
import SaveToBrandModal from '../../../my-projects/SaveToBrandModal';
import StreamModal from '../../../stream/StreamModalWrapper';
import CustomizationBreadcrumb from '../../CustomizationBreadcrumb';
import ShareVideo from '../ShareVideo';

import AddToBrand from './AddToBrandButton';
import StaffDuplicate from './StaffDuplicate';
import { CustomizationBreadcrumbWrapper, CustomizationHeaderButtons, UserMenuWrapper, Wrapper } from './Wrapper';
import { Features } from '@anm/shared/features';
import useNotStableWarning from '../../../my-projects/MyStreamsSection/hooks/useNotStableWarning';

const { urls } = config();

const {
  urls: { wave: waveUrl }
} = vtConfig();

type HeaderProps = {
  hostId: string;
  projectId: string;
};

const Header: FC<HeaderProps> = ({ hostId, projectId }) => {
  const dispatch = useDispatch();

  const [isStreamModalOpened, openStreamModal, closeStreamModal] = useSwitchState();
  const [isSaveToBrandModalOpened, openSaveToBrandModal, closeSaveToBrandModal] = useSwitchState();
  const videoData = useSelector(hostedVideoSelectors.selectVideoData);
  const canEditBrand = useSelector(userSelectors.selectCanEditBrand);
  const canStreamAndAddToBrand = !!videoData?.sources.length;
  const publishUrl = `${urls.editor}/#p=${projectId}&target=publish`;
  const canStreamHosted = useSelector(userSelectors.selectCanStreamHosted);
  const project = useSelector(projectSelectors.selectCurrentProject);

  const isPublished = !!(project?.preview_metadata[0] && hostId);

  const handleBrandChange = useCallback((brandId: number) => {
    dispatch(brandActions.updateCurrentBrandId(brandId));
  }, []);

  const publishTooltip = () => (
    <>
      This video has not been published yet.{' '}
      <a href={publishUrl} target={getLinkTarget()}>
        Publish it in the editor
      </a>{' '}
      to stream it.
    </>
  );

  const [fetchStorage, renderStorageModal, isPending] = useStorageError({
    onSuccess: openStreamModal,
    actionType: 'stream'
  });

  const { checkUpload, renderWarningModal, isNotStable } = useNotStableWarning({
    projectId,
    onSuccess: fetchStorage
  });

  const isSemrush = semrush.isSemrushIframe();

  const { t } = useTranslation();

  // demo usage of translations on VT app
  const streamDemoBtn = Features.isTranslations ? t('footer.howrotate') : 'Stream';

  const headerButtons = (
    <CustomizationHeaderButtons>
      <StaffDuplicate {...{ projectId }} />
      {canStreamHosted && (
        <NewStreamButton
          text={streamDemoBtn}
          iconName="broadcast_red"
          onClick={() => checkUpload({ showWarning: true })}
          isPending={isPending}
          variant="bordered-light-red"
          tooltipVariant="white"
          isStreamButtonDisabled={!canStreamAndAddToBrand}
          renderTooltip={!canStreamAndAddToBrand ? publishTooltip : undefined}
        />
      )}
      <AddToBrand
        {...{ publishUrl, canEditBrand }}
        isNotStable={isNotStable}
        isDisabled={!canEditBrand || !isPublished || !!isNotStable}
        onButtonClick={() => checkUpload({ cb: openSaveToBrandModal, showWarning: false })}
      />
      {!isMobileOrIPad() && <ShareVideo {...{ hostId, projectId }} />}
    </CustomizationHeaderButtons>
  );

  return (
    <>
      {renderWarningModal()}
      {isStreamModalOpened && <StreamModal onClose={closeStreamModal} isHosted {...{ projectId }} />}
      {isSaveToBrandModalOpened && (
        <SaveToBrandModal
          onBrandChange={handleBrandChange}
          onClose={closeSaveToBrandModal}
          projectId={projectId}
          fileName={videoData?.meta.name}
        />
      )}
      {renderStorageModal()}
      {!isSemrush && (
        <Wrapper>
          {!isMobileOrIPad() && <Logo variant="wave-hosting" waveUrl={waveUrl} />}
          {headerButtons}
          {!isMobileOrIPad() && (
            <UserMenuWrapper>
              <AuthUserMenuBlock size={'medium'} variant={'blue'} hideMyProjectsButton={true} />
            </UserMenuWrapper>
          )}
        </Wrapper>
      )}
      <CustomizationBreadcrumbWrapper isSemrush={isSemrush}>
        <CustomizationBreadcrumb {...{ projectId }} />
        {isSemrush && headerButtons}
      </CustomizationBreadcrumbWrapper>
    </>
  );
};

export default Header;
